import { type PropsWithChildren, createElement, forwardRef } from 'react';

import { GridItem } from './GridItem';
import { breakpointVariants, gridTemplateAreasVars, gridTemplateRowsVars, pageGridStyle } from './PageGrid.css';
import { DynamicVariantProp } from '../../types';
import { getClassNames } from '../../util/css';
import { extractDynamicStyles } from '../../util/css/vanilla-extract';

export { gridData } from './PageGrid.constants';

const sparkyClassName = 'sparky-base-grid';

/** The `PageGrid` component is *only* used for page (layout) grids */
export const PageGridComponent = forwardRef<HTMLDivElement, PropsWithChildren<Partial<Props>>>(
  ({ children, as = 'div', className = '', gridTemplateAreas, gridTemplateRows }, ref) => {
    const { classNames: dynamicCssClasses, cssVars } = extractDynamicStyles({
      gridTemplateAreas: [breakpointVariants.gridTemplateAreas, gridTemplateAreasVars, gridTemplateAreas],
      gridTemplateRows: [breakpointVariants.gridTemplateRows, gridTemplateRowsVars, gridTemplateRows],
    });

    const classNames = getClassNames(sparkyClassName, pageGridStyle, ...dynamicCssClasses, className);

    return createElement(as, { className: classNames, style: cssVars, ref }, children);
  },
);

PageGridComponent.toString = () => `.${sparkyClassName}`;
PageGridComponent.displayName = 'PageGrid';

export interface Props {
  /** This is the [native grid-template-areas CSS property](https://www.w3schools.com/cssref/pr_grid-template-areas.asp) that will be directly passed on to the grid. */
  gridTemplateAreas: DynamicVariantProp<'gridTemplateAreas'>;

  /** This is the [native grid-template-rows CSS property](https://www.w3schools.com/cssref/pr_grid-template-rows.asp) that will be directly passed on to the grid.  */
  gridTemplateRows?: DynamicVariantProp<'gridTemplateRows'>;

  /** The DOM element to render */
  as?: Extract<keyof JSX.IntrinsicElements, 'div' | 'article'>;
  className?: never;
}

export const PageGrid = Object.assign({}, PageGridComponent, { Item: GridItem });
